// cart-drawer.js - Main cart drawer component with accessibility improvements
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    
    this.handleCartUpdated = this.handleCartUpdated.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    
    this.lastFocusedElement = null;
    this.isOpen = false;
    
    this.bindEvents();
  }

  bindEvents() {
    document.querySelectorAll('[data-action="open-cart"]').forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.lastFocusedElement = document.activeElement;
        this.open();
      });
    });

    this.querySelectorAll('.drawer__close, #CartDrawer-Overlay').forEach(button => {
      button.addEventListener('click', () => {
        this.close();
      });
    });

    document.addEventListener('cart:updated', this.handleCartUpdated);
    document.addEventListener('keydown', this.handleKeydown);
    
    const cartForm = this.querySelector('#CartDrawer-Form');
    if (cartForm) {
      cartForm.addEventListener('change', (event) => {
        if (event.target.classList.contains('quantity__input') && 
            !event.target.hasAttribute('data-quantity-input')) {
          const key = event.target.dataset.index;
          const quantity = parseInt(event.target.value);
          if (key) {
            this.updateCartItemQuantity(key, quantity);
            this.announceToScreenReader(`Quantity updated to ${quantity}`);
          }
        }
      });
    }
    
    this.querySelectorAll('.quantity__button').forEach(button => {
      button.addEventListener('click', (event) => {
        const isPlus = button.getAttribute('name') === 'plus';
        const input = button.closest('quantity-input').querySelector('.quantity__input');
        const currentValue = parseInt(input.value);
        const productTitle = button.closest('.cart-item')?.querySelector('.cart-item__name')?.textContent.trim() || 'item';
        
        setTimeout(() => {
          const newValue = parseInt(input.value);
          if (newValue !== currentValue) {
            this.announceToScreenReader(`${productTitle} quantity ${isPlus ? 'increased' : 'decreased'} to ${newValue}`);
          }
        }, 100);
      });
    });
  }
  
  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      event.preventDefault();
      return;
    }
    
    if (event.key === 'Tab' && this.isOpen) {
      const focusableElements = this.getFocusableElements();
      if (focusableElements.length === 0) return;
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      if (event.shiftKey && document.activeElement === firstElement) {
        lastElement.focus();
        event.preventDefault();
      } 
      else if (!event.shiftKey && document.activeElement === lastElement) {
        firstElement.focus();
        event.preventDefault();
      }
    }
  }
  
  getFocusableElements() {
    return Array.from(this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )).filter(el => {
      return el.offsetParent !== null && !el.disabled;
    });
  }
  
  announceToScreenReader(message) {
    let liveRegion = document.getElementById('cart-drawer-announcer');
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'cart-drawer-announcer';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.classList.add('visually-hidden');
      document.body.appendChild(liveRegion);
    }
    
    liveRegion.textContent = message;
    
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 3000);
  }

  async handleCartUpdated(event) {
    if (event && event.detail && event.detail.cart) {
      await this.updateCartContents(event.detail.cart);
    } else {
      await this.updateCartContents();
    }
    
    if (event && event.detail && event.detail.doNotOpen) {
      // Don't open
    } else {
      this.open();
    }
  }

  async updateCartItemQuantity(key, quantity) {
    try {
      const lineItem = this.querySelector(`#CartDrawer-Item-${key}`);
      const loadingOverlay = lineItem?.querySelector('.loading-overlay');
      if (loadingOverlay) loadingOverlay.classList.remove('hidden');
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });
      
      if (!response.ok) throw new Error('Cart update failed');
      
      const updatedCart = await response.json();
      
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { 
          cart: updatedCart,
          doNotOpen: true
        }
      }));
      
    } catch (error) {
      const errorContainer = this.querySelector(`#CartDrawer-LineItemError-${key}`);
      if (errorContainer) {
        const errorMessage = errorContainer.querySelector('.cart-item__error-text');
        if (errorMessage) {
          errorMessage.textContent = 'Could not update quantity. Please try again.';
          errorContainer.classList.add('is-visible');
          
          this.announceToScreenReader('Error updating quantity. Please try again.');
          
          setTimeout(() => {
            errorContainer.classList.remove('is-visible');
          }, 5000);
        }
      }
    } finally {
      const lineItem = this.querySelector(`#CartDrawer-Item-${key}`);
      const loadingOverlay = lineItem?.querySelector('.loading-overlay');
      if (loadingOverlay) loadingOverlay.classList.add('hidden');
    }
  }

  async updateCartContents(cartData = null) {
    try {
      if (!cartData) {
        const response = await fetch('/cart.js');
        cartData = await response.json();
      }

      if (!cartData.items || cartData.items.length === 0) {
        this.classList.add('is-empty');
        this.announceToScreenReader('Your cart is empty');
        return;
      }

      this.classList.remove('is-empty');
      
      const subtotalElement = this.querySelector('.totals__subtotal-value');
      if (subtotalElement) {
        const formattedPrice = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(cartData.total_price / 100);
        
        subtotalElement.textContent = formattedPrice;
        this.announceToScreenReader(`Cart subtotal is ${formattedPrice}`);
      }
      
    } catch (error) {
      // Error handling
    }
  }

  open() {
    this.classList.add('active');
    this.setAttribute('aria-hidden', 'false');
    this.setAttribute('aria-expanded', 'true');
    this.style.display = 'block';
    this.style.opacity = '1';
    document.body.classList.add('overflow-hidden');
    this.isOpen = true;
    
    setTimeout(() => {
      const firstFocusable = this.getFocusableElements()[0];
      if (firstFocusable) {
        firstFocusable.focus();
      }
      
      this.announceToScreenReader('Cart drawer opened');
    }, 100);
  }

  close() {
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('aria-expanded', 'false');
    this.style.opacity = '0';
    this.isOpen = false;
    
    setTimeout(() => {
      this.style.display = 'none';
    }, 300);
    
    document.body.classList.remove('overflow-hidden');
    
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }
    
    this.announceToScreenReader('Cart drawer closed');
  }
  
  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.handleCartUpdated);
    document.removeEventListener('keydown', this.handleKeydown);
    
    const liveRegion = document.getElementById('cart-drawer-announcer');
    if (liveRegion) liveRegion.remove();
  }
}

customElements.define('cart-drawer', CartDrawer);

// gift-box-option.js - Updated for accessibility
class GiftBoxOption extends HTMLElement {
  constructor() {
    super();
    this.variantId = parseInt(this.dataset.variantId) || 41374046617687;
    this.previousValue = 0;
    this.loadingState = false;
    
    this.init();
  }

  init() {
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.plusButton = this.querySelector('button[name="plus"]');
    this.minusButton = this.querySelector('button[name="minus"]');
    
    if (this.quantityInput && !this.quantityInput.getAttribute('id')) {
      const inputId = 'GiftBoxQuantity';
      this.quantityInput.setAttribute('id', inputId);
      
      if (!this.querySelector(`label[for="${inputId}"]`)) {
        const label = document.createElement('label');
        label.setAttribute('for', inputId);
        label.classList.add('visually-hidden');
        label.textContent = 'Limited Edition Gift Box Quantity';
        this.quantityInput.parentNode.insertBefore(label, this.quantityInput);
      }
    }
    
    if (this.plusButton && !this.plusButton.getAttribute('aria-label')) {
      this.plusButton.setAttribute('aria-label', 'Increase gift box quantity');
    }
    
    if (this.minusButton && !this.minusButton.getAttribute('aria-label')) {
      this.minusButton.setAttribute('aria-label', 'Decrease gift box quantity');
    }
    
    if (this.plusButton) {
      this.plusButton.addEventListener('click', async (e) => {
        e.preventDefault();
        if (this.loadingState) return;
        await this.updateQuantity(1);
      });
    }

    if (this.minusButton) {
      this.minusButton.addEventListener('click', async (e) => {
        e.preventDefault();
        if (this.loadingState) return;
        await this.updateQuantity(-1);
      });
    }

    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', async (e) => {
        if (this.loadingState) return;
        const newValue = parseInt(e.target.value) || 0;
        const currentValue = this.previousValue;
        if (newValue !== currentValue) {
          await this.updateCart(newValue);
        }
      });
    }

    this.checkCartForGiftBox();
  }

  async checkCartForGiftBox() {
    if (!this.quantityInput) return;
    
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const giftBoxItem = cart.items.find(item => item.variant_id === this.variantId);
      
      if (giftBoxItem) {
        this.quantityInput.value = giftBoxItem.quantity;
        this.previousValue = giftBoxItem.quantity;
      } else {
        this.quantityInput.value = 0;
        this.previousValue = 0;
      }
    } catch (error) {
      // Error handling
    }
  }

  async updateQuantity(change) {
    if (this.loadingState || !this.quantityInput) return;
    
    const currentValue = parseInt(this.quantityInput.value) || 0;
    const newValue = Math.max(0, currentValue + change);
    
    if (currentValue === newValue) return;
    
    this.quantityInput.value = newValue;
    await this.updateCart(newValue);
    
    this.announceToScreenReader(`Gift box quantity ${change > 0 ? 'increased' : 'decreased'} to ${newValue}`);
  }
  
  announceToScreenReader(message) {
    let liveRegion = document.getElementById('gift-box-announcer');
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'gift-box-announcer';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.classList.add('visually-hidden');
      document.body.appendChild(liveRegion);
    }
    
    liveRegion.textContent = message;
    
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 3000);
  }

  async updateCart(quantity) {
    if (this.loadingState) return;
    
    this.loadingState = true;
    const loadingOverlay = this.closest('.drawer__gift-options')?.querySelector('.loading-overlay');
    if (loadingOverlay) loadingOverlay.classList.remove('hidden');

    try {
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      const existingItem = cartData.items.find(item => item.variant_id === this.variantId);

      let response;

      if (!existingItem && quantity > 0) {
        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: this.variantId,
              quantity: quantity
            }]
          })
        });
      } else if (existingItem && quantity !== existingItem.quantity) {
        response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: existingItem.key,
            quantity: quantity
          })
        });
      } else {
        return;
      }

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status} ${response.statusText}`);
      }

      const updatedCartResponse = await fetch('/cart.js');
      const updatedCart = await updatedCartResponse.json();

      this.quantityInput.value = quantity;
      this.previousValue = quantity;

      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { 
          cart: updatedCart,
          doNotOpen: true
        }
      }));

    } catch (error) {
      this.quantityInput.value = this.previousValue;
      this.announceToScreenReader('Error updating gift box quantity');
    } finally {
      this.loadingState = false;
      const loadingOverlay = this.closest('.drawer__gift-options')?.querySelector('.loading-overlay');
      if (loadingOverlay) loadingOverlay.classList.add('hidden');
    }
  }
  
  disconnectedCallback() {
    const liveRegion = document.getElementById('gift-box-announcer');
    if (liveRegion) liveRegion.remove();
  }
}

customElements.define('gift-box-option', GiftBoxOption);

// cart-drawer-items.js - For completeness
class CartDrawerItems extends HTMLElement {
  constructor() {
    super();
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);